import { useAuthInfo } from "@propelauth/react";
import { cn } from "../../../lib/utils";
import {
  ArrowLeft,
  Settings,
  Users,
  UsersRound,
  UserCircle,
  Shield,
  Box,
  Calculator,
  Plug,
  Key,
  Mic,
  Calendar,
  Download,
  HeartPulse,
  Layers,
  Bell,
  CheckSquare,
  Mail,
  ShieldCheck,
  Sparkles,
  Bot,
  type LucideIcon,
} from "lucide-react";
import useWorkspaceStore from "../../../store/workspace/workspaceStore";

export type SettingsSection =
  | "profile"
  | "general"
  | "security"
  | "members"
  | "teams"
  | "objects"
  | "computed-traits"
  | "connected-apps"
  | "api-keys"
  | "exports"
  | "meeting-notetaker"
  | "health-score"
  | "segments"
  | "alerts-assignment"
  | "tasks"
  | "meeting-automation"
  | "email-settings"
  | "domain-review"
  | "ai-credits"
  | "ai-connectors";

type SidebarItem = {
  id: SettingsSection;
  label: string;
  icon: LucideIcon;
  requiresRole?: string[];
  requiresPermission?: string;
};

type SidebarGroup = {
  label: string;
  items: SidebarItem[];
};

const sidebarGroups: SidebarGroup[] = [
  {
    label: "User",
    items: [
      { id: "profile", label: "Profile", icon: UserCircle },
    ],
  },
  {
    label: "Workspace",
    items: [
      { id: "general", label: "General", icon: Settings },
      { id: "security", label: "Security", icon: Shield, requiresRole: ["Owner", "Admin"] },
    ],
  },
  {
    label: "Team",
    items: [
      { id: "members", label: "Members", icon: Users },
      { id: "teams", label: "Teams", icon: UsersRound },
    ],
  },
  {
    label: "Data Model",
    items: [
      { id: "objects", label: "Objects", icon: Box },
      { id: "computed-traits", label: "Computed Traits", icon: Calculator },
    ],
  },
  {
    label: "Integrations",
    items: [
      { id: "connected-apps", label: "Connected Apps", icon: Plug },
      { id: "ai-connectors", label: "AI Connectors (MCP)", icon: Bot },
    ],
  },
  {
    label: "Email",
    items: [
      { id: "email-settings", label: "Email Settings", icon: Mail },
      { id: "domain-review", label: "Domain Review", icon: ShieldCheck },
    ],
  },
  {
    label: "Billing",
    items: [
      { id: "ai-credits", label: "AI Credits", icon: Sparkles },
    ],
  },
  {
    label: "Developer",
    items: [
      { id: "api-keys", label: "API Keys", icon: Key, requiresPermission: "can_read_api_keys" },
    ],
  },
  {
    label: "Data Management",
    items: [
      { id: "exports", label: "Exports", icon: Download },
    ],
  },
  {
    label: "Preferences",
    items: [
      { id: "segments", label: "Segments", icon: Layers },
      { id: "health-score", label: "Health Scores", icon: HeartPulse },
      { id: "alerts-assignment", label: "Alerts & Assignment", icon: Bell },
      { id: "meeting-notetaker", label: "Meeting Note Taker", icon: Mic },
      { id: "meeting-automation", label: "Meeting Automation", icon: Calendar },
      { id: "tasks", label: "Tasks", icon: CheckSquare },
    ],
  },
];

type SettingsSidebarProps = {
  activeSection: SettingsSection;
  onNavigate: (section: SettingsSection) => void;
  onBack: () => void;
  userRole?: string;
};

const SettingsSidebar = ({ activeSection, onNavigate, onBack, userRole }: SettingsSidebarProps) => {
  const { accessHelper } = useAuthInfo();
  const workspace = useWorkspaceStore((state) => state.workspace);

  return (
    <aside className="flex h-full w-[220px] shrink-0 flex-col border-r border-border bg-background">
      <div className="px-4 pb-4 pt-5">
        <button
          type="button"
          onClick={onBack}
          className="mb-4 flex items-center gap-1.5 text-[13px] text-muted-foreground transition-colors hover:text-foreground"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          <span>Settings</span>
        </button>
      </div>

      <nav className="flex-1 overflow-y-auto px-3 pb-4">
        {sidebarGroups.map((group) => {
          const visibleItems = group.items.filter(
            (item) => {
              if (item.requiresRole && userRole && !item.requiresRole.includes(userRole)) return false;
              if (item.requiresPermission && !accessHelper?.hasPermission(workspace?.id, item.requiresPermission)) return false;
              return true;
            },
          );
          if (visibleItems.length === 0) return null;
          return (
            <div key={group.label} className="mb-4">
              <span className="block px-2 pb-1 text-[11px] font-medium text-muted-foreground">
                {group.label}
              </span>
              <div className="space-y-px">
                {visibleItems.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => onNavigate(item.id)}
                    className={cn(
                      "flex w-full items-center gap-2 rounded px-2 py-1 text-left text-[13px] transition-colors",
                      activeSection === item.id
                        ? "bg-muted font-medium text-foreground"
                        : "text-muted-foreground hover:bg-muted hover:text-foreground",
                    )}
                  >
                    <item.icon className="h-4 w-4 shrink-0" />
                    {item.label}
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </nav>
    </aside>
  );
};

export default SettingsSidebar;
